import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx'; // For CSV/Excel download
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import toastify CSS

const AdminOrderDetails = () => {
  const [view, setView] = useState('current');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);

  const user = JSON.parse(localStorage.getItem('user'));
  const email = user?.email;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.post(`https://citi-mobile-app.onrender.com/admin/fetch-transactions/${email}`);
        setOrders(response.data.transactions);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to fetch orders');
        setLoading(false);
      }
    };

    if (email) {
      fetchOrders();
    }
  }, [email]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const handleCheckboxChange = (orderId) => {
    if (selectedOrders.includes(orderId)) {
      setSelectedOrders(selectedOrders.filter((id) => id !== orderId));
    } else {
      setSelectedOrders([...selectedOrders, orderId]);
    }
  };

  const handleStatusChange = async (orderId, field, value) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order._id === orderId ? { ...order, [field]: value } : order
      )
    );

    try {
      await axios.put(`https://citi-mobile-app.onrender.com/admin/update-order-status/${orderId}`, {
        email,
        [field]: value,
      });

      toast.success(`Order ${field === 'deliverystatus' ? 'delivery' : 'payment'} status updated successfully`);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(`Failed to update order ${field === 'deliverystatus' ? 'delivery' : 'payment'} status`);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await axios.delete(`https://citi-mobile-app.onrender.com/admin/delete-order/${orderId}`);
      setOrders(orders.filter((order) => order._id !== orderId));
      toast.success('Order deleted successfully');
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  const downloadOrders = () => {
    const exportData = orders.map((order) => ({
      'Order ID': order._id,
      'Customer Name': order.fullname,
      'Order Date': new Date(order.date).toLocaleDateString(),
      'Delivery Status': order.deliverystatus,
      'Payment Status': order.paymentstatus,
      'Total Amount': order.totalAmount,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
    XLSX.writeFile(workbook, 'orders.xlsx');
    toast.success('Orders downloaded successfully');
  };

  const currentOrders = orders.filter((order) => order.deliverystatus === 'processing');
  const pastOrders = orders.filter((order) => order.deliverystatus.toLowerCase() === 'delivered');

  return (
    <div className="max-w-[85%] mt-20 ml-60 max-sm:ml-0 max-sm:w-screen">
      <ToastContainer />
      <div className="flex justify-end space-x-4 mb-8">
        <button
          onClick={() => setView('current')}
          className={`px-4 py-2 rounded ${view === 'current' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'}`}
        >
          Current Orders
        </button>
        <button
          onClick={() => setView('past')}
          className={`px-4 py-2 rounded ${view === 'past' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'}`}
        >
          Past Orders
        </button>
        <button onClick={downloadOrders} className="px-4 py-2 bg-green-600 text-white rounded">
          Download Orders
        </button>
      </div>

      <div className="overflow-x-auto p-3 rounded-md w-[100%] overflow-y-auto max-h-96 shadow-md bg-white">
        <h1 className="text-[#1E1E1E] font-bold mb-5 text-lg lg:text-xl">Sales Agent Customer List</h1>

        <table className="w-[100%]">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">Select</th>
              <th className="px-4 py-2 text-left">No</th>
              <th className="px-4 py-2 text-left">Order Number/ID</th>
              <th className="px-4 py-2 text-left">Customer Name</th>
              <th className="px-4 py-2 text-left">Order Date</th>
              <th className="px-4 py-2 text-left">Order Status</th>
              <th className="px-4 py-2 text-left">Total Amount</th>
              <th className="px-4 py-2 text-left">Payment Status</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {(view === 'current' ? currentOrders : pastOrders).map((order, index) => (
              <tr key={order._id} className="border-b">
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order._id)}
                    onChange={() => handleCheckboxChange(order._id)}
                    disabled={order.deliverystatus === 'delivered'}
                  />
                </td>
                <td className="px-4 py-2">{index + 1}</td>
                <td className="px-4 py-2">{order._id}</td>
                <td className="px-4 py-2">{order.fullname}</td>
                <td className="px-4 py-2">{new Date(order.date).toLocaleDateString()}</td>
                <td className="px-4 py-2">
                  <select
                    value={order.deliverystatus}
                    onChange={(e) => handleStatusChange(order._id, 'deliverystatus', e.target.value)}
                    className="border rounded px-2 py-1"
                  >
                    <option value="processing">Processing</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </td>
                <td className="px-4 py-2">{order.totalAmount}</td>
                <td className="px-4 py-2">
                  <select
                    value={order.paymentstatus}
                    onChange={(e) => handleStatusChange(order._id, 'paymentstatus', e.target.value)}
                    className="border rounded px-2 py-1"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                  </select>
                </td>
                <td className="px-4 py-2">
                  <Link to={`/AdminOrderDetails/AdminOrderSummary/${order._id}`} className="text-blue-600">
                    View more
                  </Link>
                  <button
                    onClick={() => handleDeleteOrder(order._id)}
                    className="ml-4 text-red-600"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrderDetails;
